"use client";

import { useCallback, useRef, useEffect } from "react";
import { AppleAuthConfig, getAppleSSOConfig } from "@/utils/SSOConfig";
import { logError, logWarning } from "@/services/DatadogService";

export interface AppleAuthAuthorization {
  code: string;
  id_token: string;
  state: string;
}
export interface AppleAuthUser {
  email: string;
  name?: {
    firstName: string;
    lastName: string;
  };
}
export interface AppleAuthResponse {
  authorization: AppleAuthAuthorization;
  user?: AppleAuthUser;
}

export interface AppleIdTokenClaims {
  sub?: string;
  email?: string;
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: AppleAuthConfig) => void;
        signIn: () => Promise<AppleAuthResponse>;
      };
    };
  }
}

export interface AppleAuthOptions {
  onSuccess?: (response: AppleAuthResponse) => void;
  onError?: (error: unknown) => void;
}

export const useAppleAuth = (options?: AppleAuthOptions) => {
  const isInitialized = useRef(false);
  const optionsRef = useRef(options);
  const isProcessingRef = useRef(false);

  optionsRef.current = options;

  useEffect(() => {
    const handleSuccess = (event: any) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      optionsRef.current?.onSuccess?.(event.detail);
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 1000);
    };

    const handleFailure = (event: any) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      logWarning("Apple Sign In failed via event", {
        error: event.detail?.error,
        component: "useAppleAuth",
        action: "signInEvent",
      });
      optionsRef.current?.onError?.(event.detail?.error || new Error("Apple Sign In failed"));
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 1000);
    };

    document.addEventListener("AppleIDSignInOnSuccess", handleSuccess);
    document.addEventListener("AppleIDSignInOnFailure", handleFailure);

    return () => {
      document.removeEventListener("AppleIDSignInOnSuccess", handleSuccess);
      document.removeEventListener("AppleIDSignInOnFailure", handleFailure);
    };
  }, []);

  const signIn = useCallback(async (): Promise<AppleAuthResponse | null> => {
    if (typeof window === "undefined") return null;
    if (!window.AppleID || !window.AppleID.auth) {
      const error = new Error("Apple SDK not available on window");
      logError(error, {
        context: "AppleAuthHook",
        action: "signInPrecheck",
      });
      optionsRef.current?.onError?.(error);
      return null;
    }

    try {
      if (!isInitialized.current) {
        window.AppleID.auth.init(getAppleSSOConfig());
        isInitialized.current = true;
      }

      isProcessingRef.current = false; // Reset before new attempt
      const response = await window.AppleID.auth.signIn();

      if (!isProcessingRef.current) {
        isProcessingRef.current = true;
        optionsRef.current?.onSuccess?.(response);
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 1000);
      }
      return response;
    } catch (error) {
      if (!isProcessingRef.current) {
        isProcessingRef.current = true;
        logWarning("Apple Sign In was cancelled or failed", {
          error: error instanceof Error ? error.message : error,
          component: "useAppleAuth",
          action: "signIn",
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        optionsRef.current?.onError?.(error);
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 1000);
      }
      return null;
    }
  }, []);

  return { signIn };
};
